/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@blueprintjs/core';
import ModalDialog from 'shared/modal/dialog';
import './action-menus.scss';
import { CertStatusGridData } from 'data/certStatus/certStatus.types';

type Props = {
  name: string;
  isEditing?: boolean;
  onActivate?: () => void;
  onDelete?: () => void;
  onRequestCert?: () => void;
  certStatus?: CertStatusGridData;
  allCertStatus?: CertStatusGridData[];
};

const CertStatusActionMenus: FunctionComponent<Props> = ({
  name,
  onActivate,
  certStatus,
  isEditing,
  onRequestCert,
  allCertStatus,
  onDelete
}: Props) => {
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { t } = useTranslation();

  const handleOpenActivateDialog = () => {
    setIsActivateDialogOpen(true);
  };

  const handleCloseActivateDialog = () => {
    setIsActivateDialogOpen(false);
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  if (name == null) {
    return null;
  }

  const showDelete = () => {
    return (
      isEditing &&
      certStatus &&
      onDelete &&
      certStatus?.status === 'UNKNOWN'
    );
  };

  const showRequestCert = () => {
    const isActiveCertInXCCA = (allCertStatus || []).some((cs) => cs.status === 'DOWNLOADED' || cs.status === 'ACTIVATED');

    return (
      isEditing &&
      onRequestCert &&
      !isActiveCertInXCCA
    );
  }

  const handleActivate = () => {
    if (onActivate) {
      onActivate();
    }
    setIsActivateDialogOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsDeleteDialogOpen(false);
  };

  return (
    <div data-testid="CertStatusActionMenus" className="action-menus-container">
      {isEditing && certStatus && onActivate && certStatus?.status === 'DOWNLOADED' && (
        <Button
          className="menu-button"
          data-testid="activate-button"
          text={t('certStatus.grid.activate')}
          onClick={handleOpenActivateDialog}
        />
      )}

      {showDelete() && (
        <Button
          className="menu-button"
          data-testid="delete-button"
          text={t('certStatus.grid.delete')}
          onClick={handleOpenDeleteDialog}
        />
      )}

      {showRequestCert() && (
        <Button
          className="menu-button"
          data-testid="request-button"
          text={t('certStatus.grid.requestCert')}
          onClick={onRequestCert}
        />
      )}

      <ModalDialog
        width="500px"
        isOpen={isActivateDialogOpen}
        title="Activate Certificate"
        displayMessage={t('certStatus.activatePopup.message', { commonName: certStatus?.commonName })}
        actionText={t('certStatus.activatePopup.action')}
        onSubmit={handleActivate}
        onClose={handleCloseActivateDialog}
      />

      <ModalDialog
        width="500px"
        isOpen={isDeleteDialogOpen}
        title="Delete Certificate"
        displayMessage={t('certStatus.deletePopup.message', { commonName: certStatus?.commonName })}
        actionText={t('certStatus.deletePopup.action')}
        onSubmit={handleDelete}
        onClose={handleCloseDeleteDialog}
      />
    </div>
  );
};

export default CertStatusActionMenus;
